import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { i18next } from "@envirohub/i18n";
import { auth } from "../auth/helpers";
import { normalizeError } from "./errors";

/** Path of the cookie-based refresh endpoint (relative to baseURL). */
const REFRESH_PATH = "/auth/refresh";

/**
 * Public auth endpoints that must NOT trigger a token refresh on 401.
 * A 401 here is a business error (e.g. INVALID_CREDENTIALS), not session
 * expiry, so we surface it to the caller instead of refreshing.
 */
const NO_REFRESH_PATHS = ["/auth/login", "/auth/refresh", "/auth/register"];

/**
 * URL segment that identifies truly public (unauthenticated) endpoints.
 * Requests to these paths must:
 *  - Omit the Authorization header (avoids sending stale tokens to public routes).
 *  - Never trigger a token refresh on 401 (a 401 on a public endpoint is not a
 *    session-expiry event and must not force a login redirect).
 */
const PUBLIC_PATH_SEGMENT = "/public/";

/** Extend the request config with our retry guard flag. */
interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

function getAcceptLanguage(): string {
  if (i18next.language) return i18next.language;
  if (typeof localStorage !== "undefined") {
    return localStorage.getItem("language") ?? "en";
  }
  return "en";
}

/** Redirect to login on auth failure, guarding against redirect loops. */
function handleUnauthorized(): void {
  if (typeof window === "undefined") return;
  auth.clearAuth();
  const { pathname } = window.location;
  if (pathname !== "/login") {
    const redirect = encodeURIComponent(pathname + window.location.search);
    window.location.href = `/login?redirect=${redirect}`;
  }
}

/** Keep tokens; send the user to the forced change-password screen. */
function handlePasswordResetRequired(): void {
  if (typeof window === "undefined") return;
  const { pathname } = window.location;
  if (pathname !== "/change-password") {
    window.location.href = "/change-password";
  }
}

/** Pull tokens from the refresh response envelope. */
function extractRefreshData(
  data: unknown,
): { accessToken: string; refreshToken?: string; expiresIn?: number } | null {
  if (!data || typeof data !== "object") return null;
  const body = data as Record<string, unknown>;
  const inner = (body.data ?? body) as Record<string, unknown>;
  const token = inner.accessToken ?? inner.access_token;
  if (typeof token !== "string") return null;
  const nextRefresh = inner.refreshToken ?? inner.refresh_token;
  const expiresIn = inner.expiresIn ?? inner.expires_in;
  return {
    accessToken: token,
    refreshToken: typeof nextRefresh === "string" ? nextRefresh : undefined,
    expiresIn: typeof expiresIn === "number" ? expiresIn : undefined,
  };
}

/**
 * Call POST /auth/refresh with the stored refresh token. Updates local tokens.
 * Used after login and after change-password.
 */
export async function establishSessionAfterLogin(baseURL: string): Promise<void> {
  const refreshToken = auth.getRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }
  const response = await axios.post(`${baseURL}${REFRESH_PATH}`, { refreshToken });
  const refreshData = extractRefreshData(response.data);
  if (!refreshData) throw new Error("No access token in refresh response");
  auth.updateAccessToken(refreshData.accessToken, refreshData.expiresIn);
  if (refreshData.refreshToken) {
    auth.setRefreshToken(refreshData.refreshToken);
  }
}

/**
 * Creates an Axios instance pre-configured for the EnviroHub API.
 * Each portal supplies its own base URL from validated app env.
 */
export function createApiClient(baseURL: string): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: 20_000,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  // Single in-flight refresh shared across concurrent 401s.
  let refreshPromise: Promise<string> | null = null;

  /**
   * Call the refresh endpoint exactly once for any burst of concurrent 401s.
   * Uses a bare axios call to bypass this client's response interceptor
   * (avoids recursion).
   */
  function refreshAccessToken(): Promise<string> {
    if (!refreshPromise) {
      const refreshToken = auth.getRefreshToken();
      refreshPromise = axios
        .post(`${baseURL}${REFRESH_PATH}`, { refreshToken })
        .then((response) => {
          const refreshData = extractRefreshData(response.data);
          if (!refreshData) throw new Error("No access token in refresh response");
          auth.updateAccessToken(refreshData.accessToken, refreshData.expiresIn);
          if (refreshData.refreshToken) {
            auth.setRefreshToken(refreshData.refreshToken);
          }
          return refreshData.accessToken;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }
    return refreshPromise;
  }

  // ─── Request interceptor — attach Bearer token & language ──────────
  client.interceptors.request.use((config) => {
    /* eslint-disable no-param-reassign -- axios interceptors mutate config in place */
    if (config.headers) {
      config.headers["Accept-Language"] = getAcceptLanguage();

      const isPublicPath = config.url?.includes(PUBLIC_PATH_SEGMENT);
      if (!isPublicPath) {
        const token = auth.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    /* eslint-enable no-param-reassign */
    return config;
  });

  // ─── Response interceptor — refresh + centralized error handling ───
  // Normalizes every failure into an `ApiError` (see ./errors). Branch on
  // errorCode: SESSION_REVOKED → logout (no refresh); PASSWORD_RESET_REQUIRED
  // → /change-password; other authenticated 401s → single-flight refresh.
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableConfig | undefined;
      const status = error.response?.status;
      const apiError = normalizeError(error);

      const isPublicEndpoint = config?.url?.includes(PUBLIC_PATH_SEGMENT);
      const isRefreshableEndpoint =
        config?.url && !isPublicEndpoint && !NO_REFRESH_PATHS.some((p) => config.url!.includes(p));

      // Hard revoke: never refresh (G-4 / UC-69).
      if (apiError.errorCode === "SESSION_REVOKED" && !isPublicEndpoint) {
        handleUnauthorized();
        return Promise.reject(apiError);
      }

      // Forced reset: keep tokens (G-6 / UC-69).
      if (apiError.errorCode === "PASSWORD_RESET_REQUIRED" && !isPublicEndpoint) {
        handlePasswordResetRequired();
        return Promise.reject(apiError);
      }

      // Soft expiry: refresh once and retry (not INVALID_CREDENTIALS / EMAIL_NOT_VERIFIED).
      const isSessionExpiry =
        status === 401 &&
        apiError.errorCode !== "INVALID_CREDENTIALS" &&
        apiError.errorCode !== "EMAIL_NOT_VERIFIED";

      if (
        config &&
        /* eslint-disable-next-line no-underscore-dangle -- axios retry guard */
        !config._retry &&
        isRefreshableEndpoint &&
        isSessionExpiry &&
        auth.getToken()
      ) {
        /* eslint-disable no-underscore-dangle, no-param-reassign -- axios retry guard */
        config._retry = true;
        try {
          const token = await refreshAccessToken();
          config.headers = config.headers ?? {};
          config.headers.Authorization = `Bearer ${token}`;
          /* eslint-enable no-underscore-dangle, no-param-reassign */
          return await client(config);
        } catch (refreshError) {
          // Refresh failed (INVALID_REFRESH_TOKEN / expired cookie).
          handleUnauthorized();
          return Promise.reject(normalizeError(refreshError));
        }
      }

      if (apiError.code === "unauthorized" && !isPublicEndpoint) {
        handleUnauthorized();
      }

      return Promise.reject(apiError);
    },
  );

  return client;
}
